import {
  AtomicBreadbox,
  AtomicDidYouMean,
  AtomicFacetManager,
  AtomicLayoutSection,
  AtomicLoadMoreResults,
  AtomicNoResults,
  AtomicQueryError,
  AtomicQuerySummary,
  AtomicRefineToggle,
  AtomicSearchBox,
  AtomicSearchInterface,
  AtomicSearchLayout,
  AtomicSortDropdown,
  AtomicSortExpression,
  AtomicResultSectionVisual,
  AtomicResultImage,
  AtomicResultSectionTitle,
  AtomicResultLink,
  AtomicResultList,
  AtomicResultSectionExcerpt,
  AtomicCategoryFacet,
  AtomicSearchBoxQuerySuggestions,
  AtomicResultText,
  AtomicResultSectionBottomMetadata,
  AtomicResultPrintableUri,
  AtomicFacet,
} from "@coveo/atomic-react";
import React from "react";
import { Result } from "@coveo/headless";
import I18n from "../../../../helpers/I18n";
import { createSearchEngine } from "../../../coveo/utils/createEngine";

const renderResult = (result: Result): JSX.Element => {
  const descriptionField = result.excerpt
    ? "excerpt"
    : result.raw.description
      ? "description"
      : "ec_description";
  const imageField = result.raw.image ? "image" : "ec_images";

  return (
    <>
      {/*
        This element is rendered from a template, and so its shadow DOM isn't accessible by global CSS. Sometimes these
        templates that Coveo uses have `part` attributes that can be used to target the shadow DOM elements, but not in
        this case.
      */}

      <style>{`
      atomic-result-section-title,
      atomic-result-section-excerpt,
      atomic-result-section-bottom-metadata {
        padding: 0 20px;
      }

      atomic-result-section-excerpt,
      atomic-result-section-bottom-metadata {
        margin-top: 8px !important;
      }

      atomic-result-section-excerpt {
        max-height: 3em !important;
      }

      atomic-result-link a {
        text-decoration: none !important;
      }

      atomic-result-printable-uri a {
        color: var(--atomic-primary) !important;
      }

      atomic-result-text[field="source"],
      atomic-result-text[field="part_number"]
      {
        display: block;
        color: var(--atomic-light-text);
        font-size: 0.8rem;
      }

      atomic-result-text[field="ec_description"] {
        color: var(--atomic-lighter-text);
      }
    `}</style>
      <AtomicResultSectionVisual>
        <AtomicResultLink>
          <AtomicResultImage
            field={imageField}
            fallback="https://assets.vention.io/images/global-search/default-image-1x1.png"
          />
        </AtomicResultLink>
      </AtomicResultSectionVisual>
      <AtomicResultSectionTitle>
        <AtomicResultText field="source"/>
        <AtomicResultLink />
        <AtomicResultText field="part_number"/>
      </AtomicResultSectionTitle>
      <AtomicResultSectionExcerpt>
        <AtomicResultLink>
          <AtomicResultText field={descriptionField} />
        </AtomicResultLink>
      </AtomicResultSectionExcerpt>
      <AtomicResultSectionBottomMetadata>
        <AtomicResultPrintableUri />
      </AtomicResultSectionBottomMetadata>
    </>
  );
};

interface PageProps {
  accessToken: string;
  organizationId: string;
  locale: "en" | "fr";
}

const Page: React.FC<PageProps> = ({ accessToken, organizationId, locale }) => {
  const searchEngine = createSearchEngine(
    {
      configuration: {
        organizationId,
        accessToken,
        search: { pipeline: "Global Search" },
      },
      searchHub: "GlobalSearch",
      preprocessRequestBody: (body) => {
        body.fieldsToInclude.push("description");
        body.fieldsToInclude.push("part_number");
        body.fieldsToInclude.push("image");

        return body;
      }
    }
  );

  return (
    <AtomicSearchInterface
      languageAssetsPath="https://assets.vention.io/coveo-atomic-assets/lang"
      iconAssetsPath="https://assets.vention.io/coveo-atomic-assets/assets"
      engine={searchEngine}
      language={locale}
      localization={(i18n) => {
        const fallbackLocale = "en";

        const sourceFacetValues = I18n.t("views.global_search.facets.source.values");

        i18n.addResourceBundle(
          fallbackLocale,
          "caption-source",
          {
            "Application Pages": sourceFacetValues.application_pages,
            "Blogs": sourceFacetValues.blogs,
            "Case Studies": sourceFacetValues.case_studies,
            "Community Designs": sourceFacetValues.community_designs,
            "Datasheets": sourceFacetValues.datasheets,
            "Document360": sourceFacetValues.documents,
            "How to Guides": sourceFacetValues.how_to_guide,
            "Official Designs": sourceFacetValues.official_designs,
            "Parts": sourceFacetValues.parts,
            "Product Pages": sourceFacetValues.product_pages,
            "Tutorial Videos": sourceFacetValues.tutorial_videos,
            "User Manuals": sourceFacetValues.user_manuals,
            "Webinars": sourceFacetValues.webinars,

          },
        );

        i18n.addResourceBundle(
          fallbackLocale,
          "translation",
          I18n.t("views.global_search.sort_by"),
        );
      }}
    >
      <AtomicSearchLayout id="global-search-page" className="search-page">
        <AtomicLayoutSection section="search">
          <div className="search-page__header">
            <div className="search-page__section-content">
              <h2 className="search-page__title">{I18n.t("views.global_search.title")}</h2>
              <AtomicSearchBox>
                <AtomicSearchBoxQuerySuggestions />
                <AtomicDidYouMean />
              </AtomicSearchBox>
            </div>
          </div>
        </AtomicLayoutSection>
        <div className="search-page__search-results search-page__section-content">
          <div className="search-page__facets">
            <AtomicLayoutSection section="facets">
              <AtomicFacetManager>
                <AtomicFacet
                  field="source"
                  label={I18n.t("views.global_search.facets.source.label")}
                />
              </AtomicFacetManager>
            </AtomicLayoutSection>
          </div>
          <div className="search-page__main">
            <AtomicLayoutSection section="main">
              <AtomicLayoutSection section="status">
                <AtomicBreadbox />
                <AtomicQuerySummary />
                <AtomicRefineToggle />
                <AtomicSortDropdown>
                  <AtomicSortExpression
                    label="relevance"
                    expression="relevancy"
                  />
                  <AtomicSortExpression
                    label="most_recent"
                    expression="date descending"
                  />
                </AtomicSortDropdown>
              </AtomicLayoutSection>
              <AtomicLayoutSection section="results">
                <AtomicResultList
                  display="list"
                  imageSize="small"
                  template={(result) => ({
                    contentTemplate: renderResult(result),
                    linkTemplate: <></>,
                  })}
                />
                <AtomicQueryError />
                <AtomicNoResults />
              </AtomicLayoutSection>
              <AtomicLayoutSection section="pagination">
                <AtomicLoadMoreResults />
              </AtomicLayoutSection>
            </AtomicLayoutSection>
          </div>
        </div>
      </AtomicSearchLayout>
    </AtomicSearchInterface>
  );
};

export default Page;
